/**
 * AVISO DA ASSINATURA (CERES-ACC-005).
 *
 * Quem entra numa organização criada pelo cadastro público e ainda sem
 * ativação vê o CRM sem recursos comerciais — o gate recusa no servidor. Sem
 * este aviso, a tela pareceria quebrada. Diz a verdade: aguardando ativação, ou
 * avaliação gratuita com data de fim. Nas demais situações, nada aparece.
 *
 * CERES-ACC-005-OPER-001: o aviso diz também QUAL plano foi escolhido e o que
 * já dá para fazer enquanto a ativação não vem — convidar a equipe (a
 * administração do tenant não depende da assinatura). Nunca "ativa" antes da
 * hora: pendente continua sendo pendente.
 *
 * Falha silenciosa de propósito: um aviso que não carregou não pode virar erro
 * na tela de quem só quer trabalhar.
 */

import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { t } from '../lib/i18n';
import { useSession } from '../lib/session';
import { formatDate } from '../lib/format';

interface VisaoDoPlano {
  plano: { code: string; name: string } | null;
  situacao: string | null;
  fimDaVigencia: string | null;
}

export function AvisoDaAssinatura({
  organizationId,
}: {
  organizationId: string;
}): JSX.Element | null {
  const { canAny } = useSession();
  const visao = useQuery({
    queryKey: ['aviso-da-assinatura', organizationId],
    queryFn: () => api.get<VisaoDoPlano>('/plano'),
    retry: false,
    staleTime: 5 * 60_000,
    enabled: organizationId !== '',
  });
  const situacao = visao.data?.situacao ?? null;
  if (situacao !== 'PENDING' && !(situacao === 'TRIAL' && visao.data?.fimDaVigencia != null))
    return null;

  const plano = visao.data?.plano?.name ?? '';
  const texto =
    situacao === 'PENDING'
      ? t('assinatura.avisoPendente').replace('{plano}', plano)
      : t('assinatura.avisoAvaliacao')
          .replace('{plano}', plano)
          .replace('{data}', formatDate(visao.data?.fimDaVigencia));
  const podeConvidar = canAny(['user:manage']);
  return (
    <div
      role="status"
      className="mb-4 flex flex-wrap items-center gap-2 rounded border border-line bg-surface-sunken px-3 py-2 text-sm text-ink"
    >
      <span className="flex-1">{texto}</span>
      {situacao === 'PENDING' && podeConvidar && (
        <Link to="/admin/organizacao?aba=convites" className="text-xs font-medium text-brand-600 underline">
          {t('assinatura.convidarEquipe')}
        </Link>
      )}
      <Link to="/plano" className="text-xs font-medium text-brand-600 underline">
        {t('assinatura.verPlano')}
      </Link>
    </div>
  );
}
